import { useTranslation } from 'react-i18next'

import { ProjectContact } from '@types'

import { Input } from '@components/Input'

import AddIcon from '@mui/icons-material/AddRounded'
import trashIcon from '@assets/images/trash-icon.svg'
import arrowIcon from '@assets/images/arrow_up-icon.svg'

import {
  AddButton,
  ButtonContainer,
  CardContact,
  CardHeader,
  Container,
  List,
  Row,
  MoveButtonsContainer,
  MoveButton,
  ArrowUpImg,
  ArrowDownImg,
} from './ContactList.styled'

type ContactListProps = {
  contacts: ProjectContact[]
  onUpdate: (id: string, data: Partial<ProjectContact>) => void
  onRemove: (id: string) => void
  onAdd: () => void
  onMoveUp?: (id: string) => void
  onMoveDown?: (id: string) => void
}

export const ContactList: React.FC<ContactListProps> = ({
  contacts,
  onAdd,
  onRemove,
  onUpdate,
  onMoveUp,
  onMoveDown,
}: ContactListProps) => {
  const { t } = useTranslation()

  const handleMoveContact = (contactId: string, direction: 'up' | 'down') => {
    if (direction === 'up' && onMoveUp) {
      onMoveUp(contactId)
    } else if (direction === 'down' && onMoveDown) {
      onMoveDown(contactId)
    }
  }

  return (
    <Container>
      <List>
        {contacts.map((contact, index, array) => (
          <CardContact key={contact.id}>
            <CardHeader>
              {(onMoveUp || onMoveDown) && (
                <MoveButtonsContainer>
                  <MoveButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleMoveContact(contact.id, 'up')
                    }}
                    disabled={index === 0}
                  >
                    <ArrowUpImg src={arrowIcon} />
                  </MoveButton>
                  <MoveButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleMoveContact(contact.id, 'down')
                    }}
                    disabled={index === array.length - 1}
                  >
                    <ArrowDownImg src={arrowIcon} />
                  </MoveButton>
                </MoveButtonsContainer>
              )}
              <ButtonContainer
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onRemove(contact.id)
                }}
              >
                <img src={trashIcon} />
              </ButtonContainer>
            </CardHeader>
            <Input
              className="input"
              autoFocus
              required
              label={t('modules_project_components_ContactList_role_label')}
              placeholder={t('modules_project_components_ContactList_role_placeholder')}
              value={contact.role}
              maxLength={255}
              onChange={(role) => onUpdate?.(contact.id, { ...contact, role })}
            />

            <Input
              className="input"
              required
              label={t('modules_project_components_ContactList_company_name_label')}
              placeholder={t('modules_project_components_ContactList_company_name_placeholder')}
              value={contact.companyName}
              maxLength={255}
              onChange={(companyName) => onUpdate?.(contact.id, { ...contact, companyName })}
            />

            <Input
              className="input"
              label={t('modules_project_components_ContactList_address_label')}
              placeholder={t('modules_project_components_ContactList_address_placeholder')}
              value={contact.address}
              maxLength={255}
              onChange={(address) => onUpdate?.(contact.id, { ...contact, address })}
            />

            <Row>
              <Input
                className="input"
                label={t('modules_project_components_ContactList_zip_label')}
                placeholder={t('modules_project_components_ContactList_zip_placeholder')}
                value={contact.zipCode}
                maxLength={255}
                onChange={(zipCode) => onUpdate?.(contact.id, { ...contact, zipCode })}
              />

              <Input
                className="input"
                label={t('modules_project_components_ContactList_city_label')}
                placeholder={t('modules_project_components_ContactList_city_placeholder')}
                value={contact.city}
                maxLength={255}
                onChange={(city) => onUpdate?.(contact.id, { ...contact, city })}
              />
            </Row>

            <Input
              className="input"
              label={t('modules_project_components_ContactList_name_label')}
              placeholder={t('modules_project_components_ContactList_name_placeholder')}
              value={contact.fullName}
              maxLength={255}
              onChange={(fullName) => onUpdate?.(contact.id, { ...contact, fullName })}
            />

            <Input
              className="input"
              label={t('modules_project_components_ContactList_phone_label')}
              placeholder={t('modules_project_components_ContactList_phone_placeholder')}
              value={contact.phone}
              type="tel"
              onChange={(phone) => onUpdate?.(contact.id, { ...contact, phone })}
            />

            <Input
              className="input"
              label={t('modules_project_components_ContactList_email_label')}
              placeholder={t('modules_project_components_ContactList_email_placeholder')}
              value={contact.email}
              type="email"
              maxLength={255}
              onChange={(email) => onUpdate?.(contact.id, { ...contact, email })}
            />
          </CardContact>
        ))}
      </List>

      <AddButton type="button" onClick={onAdd}>
        <AddIcon />
        <span>{t('modules_project_components_ContactList_add_label')}</span>
      </AddButton>
    </Container>
  )
}
